(() => {
  'use strict';

  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const campaignKey = /^(utm_[a-z0-9_]+|gclid|fbclid|ttclid|msclkid|srsltid)$/i;

  function endpoint(root, path) {
    const base = root.endsWith('/') ? root : `${root}/`;
    return `${base}${path.replace(/^\//, '')}`;
  }

  async function jsonRequest(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.body ? { 'Content-Type': 'application/json' } : {}) },
      ...options,
    });
    let payload = null;
    try { payload = await response.json(); } catch (_) { /* A non-JSON error is handled below. */ }
    if (!response.ok) {
      const detail = payload?.description || payload?.message || `Błąd ${response.status}`;
      throw new Error(detail);
    }
    if (!payload) throw new Error('Sklep nie zwrócił potwierdzenia operacji.');
    return payload;
  }

  function checkoutPath(root) {
    const checkout = new URL(endpoint(root, 'checkout'), window.location.origin);
    const current = new URL(window.location.href);
    current.searchParams.forEach((value, key) => {
      if (campaignKey.test(key) && value.length <= 300 && !/[\r\n]/.test(value)) checkout.searchParams.set(key, value);
    });
    return `${checkout.pathname}${checkout.search}`;
  }

  function cartMutation(cart, variantId, quantity) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const exactLines = items.filter((item) => String(item.variant_id) === String(variantId));
    if (!exactLines.length) {
      return { path: 'cart/add.js', payload: { items: [{ id: Number(variantId), quantity }] } };
    }
    if (exactLines.length === 1) {
      return { path: 'cart/change.js', payload: { id: exactLines[0].key, quantity } };
    }
    const updates = {};
    exactLines.forEach((item, index) => { updates[item.key] = index === 0 ? quantity : 0; });
    return { path: 'cart/update.js', payload: { updates } };
  }

  function variantQuantity(payload, variantId) {
    const items = Array.isArray(payload.items) ? payload.items : [];
    return items
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  async function reconcileArmQuantity(rootUrl, variantId, quantity) {
    const cart = await jsonRequest(endpoint(rootUrl, 'cart.js'), { cache: 'no-store' });
    const mutation = cartMutation(cart, variantId, quantity);
    const result = await jsonRequest(endpoint(rootUrl, mutation.path), {
      method: 'POST',
      body: JSON.stringify(mutation.payload),
    });
    if (variantQuantity(result, variantId) !== quantity) {
      throw new Error('Koszyk nie potwierdził wybranej liczby urządzeń.');
    }
    return result;
  }

  function initSale(root) {
    if (root.dataset.armReady === 'true') return;
    root.dataset.armReady = 'true';

    const scope = root.closest('main') || root;
    const form = scope.querySelector('[data-arm-form]');
    const radios = [...scope.querySelectorAll('[data-arm-quantity-input]')];
    const mirrors = [...scope.querySelectorAll('[data-arm-mirror-quantity]')];
    const quantityText = [...scope.querySelectorAll('[data-arm-qty-text]')];
    const submitButtons = [...scope.querySelectorAll('[data-arm-submit]')];
    const error = root.querySelector('[data-arm-error]');
    const rootUrl = root.dataset.rootUrl || '/';
    const variantId = root.dataset.variantId;
    let submitting = false;

    function selectedQuantity() {
      return Number(radios.find((radio) => radio.checked)?.value || root.dataset.quantity || 1) === 2 ? 2 : 1;
    }

    function setQuantity(quantity) {
      const value = Number(quantity) === 2 ? 2 : 1;
      radios.forEach((radio) => { radio.checked = Number(radio.value) === value; });
      mirrors.forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.armMirrorQuantity) === value)));
      quantityText.forEach((node) => { node.textContent = `×${value}`; });
      root.dataset.quantity = String(value);
      scope.querySelectorAll('[data-arm-quantity-state]').forEach((node) => { node.dataset.quantity = String(value); });
      if (error) { error.hidden = true; error.textContent = ''; }
    }

    function setBusy(busy) {
      submitting = busy;
      submitButtons.forEach((button) => {
        button.disabled = busy;
        button.setAttribute('aria-busy', String(busy));
        const label = button.querySelector('[data-arm-submit-label]');
        if (!label) return;
        if (!label.dataset.original) label.dataset.original = label.textContent;
        label.textContent = busy ? 'PRZYGOTOWUJĘ KASĘ…' : label.dataset.original;
      });
      mirrors.forEach((button) => { button.disabled = busy; });
      radios.forEach((radio) => { radio.disabled = busy; });
    }

    radios.forEach((radio) => radio.addEventListener('change', () => setQuantity(radio.value)));
    mirrors.forEach((button) => button.addEventListener('click', () => {
      setQuantity(button.dataset.armMirrorQuantity);
    }));

    if (form) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (submitting) return;
        const quantity = selectedQuantity();
        setBusy(true);
        if (error) { error.hidden = true; error.textContent = ''; }
        try {
          await reconcileArmQuantity(rootUrl, variantId, quantity);
          window.location.assign(checkoutPath(rootUrl));
        } catch (requestError) {
          console.error('Noom Arm checkout preparation failed:', requestError);
          if (error) {
            error.textContent = `Nie udało się przygotować koszyka. ${requestError.message || 'Spróbuj ponownie za chwilę.'}`;
            error.hidden = false;
            error.scrollIntoView({ behavior: motion.matches ? 'auto' : 'smooth', block: 'center' });
          }
          setBusy(false);
        }
      });
    }

    const sticky = scope.querySelector('[data-arm-sticky]');
    const zones = [...scope.querySelectorAll('[data-arm-buy-zone]')];
    if (sticky && zones.length && 'IntersectionObserver' in window) {
      const visible = new Set();
      const stickyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target));
        sticky.classList.toggle('is-hidden', visible.size > 0);
      }, { threshold: .08 });
      zones.forEach((zone) => stickyObserver.observe(zone));
    }

    scope.querySelectorAll('[data-arm-disclosures]').forEach((group) => {
      group.addEventListener('toggle', (event) => {
        if (!(event.target instanceof HTMLDetailsElement) || !event.target.open) return;
        group.querySelectorAll('details[open]').forEach((item) => { if (item !== event.target) item.open = false; });
      }, true);
    });

    const reset = () => setBusy(false);
    window.addEventListener('pageshow', reset);
    setQuantity(selectedQuantity());
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-arm-sale]').forEach(initSale);
  }

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
